import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
    Plane, Plus, Loader2, Check, X, Trash2, UserCheck, AlertTriangle,
} from "lucide-react";

const STATUS_BADGES = {
    richiesta: { label: "IN ATTESA", cls: "bg-amber-100 text-amber-700" },
    approvata: { label: "APPROVATA", cls: "bg-emerald-100 text-emerald-700" },
    rifiutata: { label: "RIFIUTATA", cls: "bg-red-100 text-red-600" },
};
const fmtDate = (iso) => {
    try {
        return new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
};

export default function FeriePage() {
    const [data, setData] = useState(null);
    const [gaps, setGaps] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [richiesteEnabled, setRichiesteEnabled] = useState(false);
    const [filterStatus, setFilterStatus] = useState("");
    const [showCreate, setShowCreate] = useState(false);

    const load = useCallback(async () => {
        try {
            const [l, g, c] = await Promise.all([
                api.get("/presenze/leave", { params: filterStatus ? { status: filterStatus } : {} }),
                api.get("/presenze/substitutions/gaps"),
                api.get("/presenze/richieste-config"),
            ]);
            setData(l.data);
            setGaps(g.data);
            setRichiesteEnabled(!!c.data.enabled);
        } catch { toast.error("Errore nel caricamento"); }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/presenze/planning/employees").then((r) => setEmployees(r.data)).catch(() => {});
    }, []);

    const toggleRichieste = async (v) => {
        try {
            await api.post("/presenze/richieste-config", { enabled: v });
            setRichiesteEnabled(v);
            toast.success(v ? "Richieste dipendenti ATTIVATE nel portale" : "Richieste dipendenti disattivate");
        } catch { toast.error("Errore nel salvataggio"); }
    };

    const decide = async (l, status) => {
        try {
            await api.post(`/presenze/leave/${l.id}/decide`, { status });
            toast.success(status === "approvata" ? "Richiesta approvata" : "Richiesta rifiutata");
            load();
        } catch { toast.error("Errore"); }
    };

    const remove = async (l) => {
        if (!window.confirm(`Eliminare la richiesta di ${l.employee_name}?`)) return;
        try {
            await api.delete(`/presenze/leave/${l.id}`);
            toast.success("Richiesta eliminata");
            load();
        } catch { toast.error("Errore nell'eliminazione"); }
    };

    if (!data || !gaps) {
        return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
    }

    return (
        <div className="mx-auto max-w-6xl px-6 py-8" data-testid="ferie-page">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <Plane className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Ferie e permessi</h1>
                        <p className="text-sm text-slate-500">Richieste, approvazioni e sostituzioni</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600" data-testid="ferie-richieste-toggle">
                        Richieste dai dipendenti: {richiesteEnabled ? <span className="text-emerald-600">ATTIVE</span> : <span className="text-slate-400">DISATTIVE</span>}
                        <Switch checked={richiesteEnabled} onCheckedChange={toggleRichieste} data-testid="ferie-richieste-switch" />
                    </label>
                    <Button onClick={() => setShowCreate(true)} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="ferie-add-btn">
                        <Plus className="h-4 w-4" /> Nuova richiesta
                    </Button>
                </div>
            </div>

            <GapsCard gaps={gaps} employees={employees} onChanged={load} />

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
                    <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-600">
                        Richieste {data.pending > 0 && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700" data-testid="ferie-pending-badge">{data.pending} in attesa</span>}
                    </h2>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                        className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="ferie-filter-status">
                        <option value="">Tutti gli stati</option>
                        <option value="richiesta">In attesa</option>
                        <option value="approvata">Approvate</option>
                        <option value="rifiutata">Rifiutate</option>
                    </select>
                </div>
                {data.items.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500" data-testid="ferie-empty">Nessuna richiesta presente.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                                <tr>
                                    <th className="px-3 py-2.5">Dipendente</th>
                                    <th className="px-3 py-2.5">Tipo</th>
                                    <th className="px-3 py-2.5">Periodo</th>
                                    <th className="px-3 py-2.5">Note</th>
                                    <th className="px-3 py-2.5">Stato</th>
                                    <th className="px-3 py-2.5">Origine</th>
                                    <th className="px-3 py-2.5 text-right">Azioni</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {data.items.map((l) => {
                                    const b = STATUS_BADGES[l.status] || {};
                                    return (
                                        <tr key={l.id} className="hover:bg-slate-50/60" data-testid={`ferie-row-${l.id}`}>
                                            <td className="px-3 py-2 font-medium text-slate-700">{l.employee_name}
                                                {l.matricola && <span className="ml-1.5 text-[11px] font-normal text-slate-400">matr. {l.matricola}</span>}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${l.type === "ferie" ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"}`}>
                                                    {l.type === "ferie" ? "FERIE" : "PERMESSO"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-xs text-slate-600">
                                                {l.date_from === l.date_to ? fmtDate(l.date_from) : `${fmtDate(l.date_from)} → ${fmtDate(l.date_to)}`}
                                                {l.time_from && <span className="ml-1 font-mono-numbers text-slate-500">({l.time_from}–{l.time_to || "…"})</span>}
                                            </td>
                                            <td className="max-w-[180px] truncate px-3 py-2 text-xs text-slate-500" title={l.note || ""}>{l.note || "—"}</td>
                                            <td className="px-3 py-2">
                                                <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${b.cls}`} data-testid={`ferie-status-${l.id}`}>{b.label}</span>
                                            </td>
                                            <td className="px-3 py-2 text-xs text-slate-500">{l.created_by === "dipendente" ? "Dipendente" : "Ufficio"}</td>
                                            <td className="px-3 py-2">
                                                <div className="flex justify-end gap-1">
                                                    {l.status === "richiesta" && (
                                                        <>
                                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" title="Approva" onClick={() => decide(l, "approvata")} data-testid={`ferie-approve-${l.id}`}>
                                                                <Check className="h-4 w-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" title="Rifiuta" onClick={() => decide(l, "rifiutata")} data-testid={`ferie-reject-${l.id}`}>
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" title="Elimina" onClick={() => remove(l)} data-testid={`ferie-delete-${l.id}`}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <CreateLeaveDialog open={showCreate} employees={employees}
                onClose={() => setShowCreate(false)}
                onSaved={() => { setShowCreate(false); load(); }} />
        </div>
    );
}

function GapsCard({ gaps, employees, onChanged }) {
    const [sel, setSel] = useState({});
    const [savingIdx, setSavingIdx] = useState(null);

    const assign = async (g, idx) => {
        const empId = sel[idx];
        if (!empId) { toast.error("Seleziona il sostituto"); return; }
        setSavingIdx(idx);
        try {
            await api.post("/presenze/substitutions", {
                leave_id: g.leave_id, date: g.date, activity_id: g.activity_id,
                shift_id: g.shift_id, substitute_employee_id: empId,
            });
            toast.success("Sostituzione assegnata");
            onChanged();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore");
        } finally { setSavingIdx(null); }
    };

    const removeSub = async (g) => {
        try {
            await api.delete(`/presenze/substitutions/${g.substitution.id}`);
            toast.success("Sostituzione rimossa");
            onChanged();
        } catch { toast.error("Errore"); }
    };

    return (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="gaps-card">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-slate-600">
                    <UserCheck className="h-4 w-4 text-[#376DB3]" /> Sostituzioni — prossimi 14 giorni
                </h2>
                {gaps.uncovered > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700" data-testid="gaps-uncovered-badge">
                        <AlertTriangle className="h-3.5 w-3.5" /> {gaps.uncovered} turni da coprire
                    </span>
                ) : (
                    <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">Tutto coperto</span>
                )}
            </div>
            {gaps.gaps.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500" data-testid="gaps-empty">
                    Nessuna assenza approvata con turni pianificati nei prossimi 14 giorni.
                </div>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {gaps.gaps.map((g, idx) => (
                        <li key={`${g.date}-${g.shift_id}-${g.absent_employee_id}`} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm" data-testid={`gap-row-${idx}`}>
                            <div className="min-w-0">
                                <span className="font-semibold capitalize text-slate-700">{fmtDate(g.date)}</span>
                                <span className="ml-2 text-slate-600">{g.absent_name}</span>
                                <span className={`ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-bold ${g.leave_type === "ferie" ? "bg-sky-100 text-sky-700" : "bg-violet-100 text-violet-700"}`}>
                                    {g.leave_type === "ferie" ? "FERIE" : "PERMESSO"}
                                </span>
                                <div className="text-xs text-slate-500">
                                    {g.activity_name} · {g.worksite_name} · <span className="font-mono-numbers">{g.shift_start}–{g.shift_end}</span>
                                </div>
                            </div>
                            {g.substitution ? (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid={`gap-covered-${idx}`}>
                                    <Check className="h-3.5 w-3.5" /> {g.substitution.substitute_name}
                                    <button type="button" className="text-emerald-400 hover:text-red-500" title="Rimuovi sostituzione" onClick={() => removeSub(g)} data-testid={`sub-remove-${idx}`}>
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </span>
                            ) : (
                                <div className="flex items-center gap-1.5">
                                    <select value={sel[idx] || ""} onChange={(e) => setSel((p) => ({ ...p, [idx]: e.target.value }))}
                                        className="h-8 rounded-md border border-amber-300 bg-amber-50/50 px-2 text-xs" data-testid={`sub-select-${idx}`}>
                                        <option value="">Scegli sostituto…</option>
                                        {employees.filter((e) => e.id !== g.absent_employee_id).map((e) => (
                                            <option key={e.id} value={e.id}>{e.cognome} {e.nome}</option>
                                        ))}
                                    </select>
                                    <Button size="sm" className="h-8 bg-[#376DB3] text-xs text-white hover:bg-[#2c5a96]" disabled={savingIdx === idx}
                                        onClick={() => assign(g, idx)} data-testid={`sub-assign-${idx}`}>
                                        {savingIdx === idx ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Assegna"}
                                    </Button>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function CreateLeaveDialog({ open, employees, onClose, onSaved }) {
    const [employeeId, setEmployeeId] = useState("");
    const [type, setType] = useState("ferie");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [timeFrom, setTimeFrom] = useState("");
    const [timeTo, setTimeTo] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setEmployeeId(""); setType("ferie"); setDateFrom(""); setDateTo("");
            setTimeFrom(""); setTimeTo(""); setNote("");
        }
    }, [open]);

    const save = async () => {
        if (!employeeId) { toast.error("Seleziona il dipendente"); return; }
        if (!dateFrom) { toast.error("Inserisci la data di inizio"); return; }
        setSaving(true);
        try {
            await api.post("/presenze/leave", {
                employee_id: employeeId, type,
                date_from: dateFrom, date_to: dateTo || dateFrom,
                time_from: timeFrom || null, time_to: timeTo || null,
                note: note || null,
            });
            toast.success("Assenza registrata (approvata)");
            onSaved();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-md" data-testid="ferie-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-slate-800">Nuova richiesta (ufficio)</DialogTitle>
                    <DialogDescription>Le assenze inserite dall'ufficio risultano subito approvate.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Dipendente *</Label>
                        <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                            className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="ferie-employee-select">
                            <option value="">Seleziona…</option>
                            {employees.map((e) => <option key={e.id} value={e.id}>{e.cognome} {e.nome}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Tipo</Label>
                            <select value={type} onChange={(e) => setType(e.target.value)}
                                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="ferie-type-select">
                                <option value="ferie">Ferie</option>
                                <option value="permesso">Permesso</option>
                            </select>
                        </div>
                        {type === "permesso" && (
                            <div className="grid grid-cols-2 gap-1.5">
                                <div>
                                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Dalle</Label>
                                    <input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)}
                                        className="mt-1 h-9 w-full rounded-md border border-slate-200 px-1.5 text-sm" data-testid="ferie-time-from" />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Alle</Label>
                                    <input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)}
                                        className="mt-1 h-9 w-full rounded-md border border-slate-200 px-1.5 text-sm" data-testid="ferie-time-to" />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Dal *</Label>
                            <Input type="date" className="mt-1" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="ferie-date-from" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Al</Label>
                            <Input type="date" className="mt-1" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="ferie-date-to" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Note</Label>
                        <Input className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Facoltative" data-testid="ferie-note-input" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose}>Annulla</Button>
                    <Button onClick={save} disabled={saving} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="ferie-save-btn">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Salva
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
